import { useEffect, useState } from "react";

import { api, type Routine, type RoutineStart, type Today } from "@/api";
import { Button } from "@/components/ui/button";
import { ErrorAlert } from "@/components/error-alert";
import { formatMinutes } from "@/lib/color";
import { cn } from "@/lib/utils";

function messageOf(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback;
}

function tomorrowISO(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** The next day's planned events, shown when starting an evening Routine. */
function TomorrowSummary() {
  const [data, setData] = useState<Today | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let live = true;
    api
      .today(tomorrowISO())
      .then((t) => live && setData(t))
      .catch(() => live && setFailed(true));
    return () => {
      live = false;
    };
  }, []);

  const clock = (iso: string) =>
    new Intl.DateTimeFormat(undefined, {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: data?.time_zone,
    }).format(new Date(iso));

  return (
    <section aria-label="Tomorrow" className="rounded-lg bg-secondary/60 p-3">
      <h3 className="text-sm font-medium">Tomorrow</h3>
      {failed ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Could not load tomorrow&apos;s plan.
        </p>
      ) : data === null ? (
        <p className="mt-1 text-xs text-muted-foreground">Loading…</p>
      ) : data.activities.length === 0 ? (
        <p className="mt-1 text-xs text-muted-foreground">
          Nothing planned yet.
        </p>
      ) : (
        <>
          <p className="text-xs text-muted-foreground">
            {data.activities.length} planned ·{" "}
            {formatMinutes(data.planned_minutes)}
          </p>
          <ul className="mt-2 space-y-0.5 text-sm">
            {data.activities.map((a) => (
              <li key={a.id} className="flex gap-2">
                <span className="tabular-nums text-muted-foreground">
                  {clock(a.start_at)}
                </span>
                <span className="min-w-0 flex-1 truncate">{a.title}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

/**
 * Start a Routine: tick the Actions to do now (all by default), see the
 * total, then start. Starting writes one Calendar event of the chosen
 * Actions' total length and opens the timer.
 */
export function StartRoutineDialog({
  routine,
  onClose,
  onStarted,
}: {
  routine: Routine;
  onClose: () => void;
  onStarted: (result: RoutineStart) => Promise<void>;
}) {
  const ids = routine.actions.map((a) => a.id ?? a.title);
  const [picked, setPicked] = useState<Set<string>>(() => new Set(ids));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const chosen = routine.actions.filter((a) => picked.has(a.id ?? a.title));
  const total = chosen.reduce((s, a) => s + a.duration_minutes, 0);
  const allPicked = chosen.length === routine.actions.length;

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function start() {
    setBusy(true);
    setError(null);
    try {
      const actionIds = allPicked ? [] : chosen.map((a) => a.id ?? "");
      const result = await api.startRoutine(routine.id, actionIds);
      await onStarted(result);
    } catch (err) {
      setError(messageOf(err, "could not start the routine"));
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-foreground/40 p-4 sm:p-8"
      role="dialog"
      aria-modal="true"
      aria-labelledby="start-routine-title"
      onClick={(ev) => {
        if (ev.target === ev.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-md space-y-4 rounded-2xl border border-border bg-card p-5 shadow-xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 id="start-routine-title" className="text-lg font-semibold">
              Start {routine.title}
            </h2>
            <p className="text-sm text-muted-foreground">
              Tick what you will do now. It becomes one calendar block from now.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setPicked(allPicked ? new Set() : new Set(ids))}
          >
            {allPicked ? "Clear" : "All"}
          </Button>
        </div>

        <ul className="divide-y divide-border text-sm">
          {routine.actions.map((a) => {
            const id = a.id ?? a.title;
            const on = picked.has(id);
            return (
              <li key={id}>
                <label
                  className={cn(
                    "flex cursor-pointer items-center gap-3 py-2",
                    !on && "text-muted-foreground",
                  )}
                >
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-primary"
                    checked={on}
                    onChange={() => toggle(id)}
                  />
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate",
                      !on && "line-through",
                    )}
                  >
                    {a.title}
                  </span>
                  <span className="tabular-nums">{a.duration_minutes} min</span>
                </label>
              </li>
            );
          })}
        </ul>

        <div className="flex items-baseline justify-between text-sm">
          <span className="text-muted-foreground">
            {chosen.length} of {routine.actions.length} actions
          </span>
          <span className="text-base font-semibold tabular-nums">
            {total > 0 ? formatMinutes(total) : "—"}
          </span>
        </div>

        {routine.type === "evening" && <TomorrowSummary />}

        {error && <ErrorAlert message={error} />}
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => void start()}
            disabled={busy || chosen.length === 0}
          >
            Start now
          </Button>
        </div>
      </div>
    </div>
  );
}
